import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Download, Eye, FileText, CheckCircle, PlusCircle, FileDigit, Send, Layers } from 'lucide-react';
import WealthChatBot from '../../components/WealthChatBot';
import AuthorWidgetComponent from './components/AuthorWidgetComponent';
import AddAuthorWidgetPopup from './components/AddAuthorWidgetPopup';
import AuthorSidebar from './components/AuthorSidebar';
import AuthorHeader from './components/AuthorHeader';
import authoringService from './api';

const DEFAULT_WIDGETS = [
    {
        id: 1,
        title: "Title",
        prompt: "Write a headline about navigating downturns with a smart, long-term investment mindset.",
        chartQuery: "",
        content: ""
    },
    {
        id: 2,
        title: "Introduction",
        prompt: "Compose an introduction that balances empathy with optimism, encouraging readers to keep perspective during market pullbacks",
        chartQuery: "",
        content: ""
    },
    {
        id: 3,
        title: "1. Market declines are part of investing",
        prompt: "Write the first paragraph and explain why market downturns are normal and often temporary, helping readers put current volatility into perspective.",
        chartQuery: "",
        content: ""
    },
    {
        id: 4,
        title: "2. Time in the market matters, not market timing",
        prompt: "Write the second paragraph, describing how emotional decision-making during downturns often leads to worse financial outcomes.",
        chartQuery: "",
        content: ""
    },
    // {
    //     id: 5,
    //     title: "Missing just a few days in the market can hurt market returns",
    //     prompt: "Display a chart showing the value in USD for each investment scenario in the SP500 dataset",
    //     chartQuery: "Give me a bar chart",
    //     content: ""
    // },
    {
        id: 5,
        title: "Conclusion",
        prompt: "Write the first paragraph and explain why market downturns are normal and often temporary, helping readers put current volatility into perspective.",
        chartQuery: "",
        content: ""
    }
];

const AuthorPage = () => {
    const [customWidgets, setCustomWidgets] = useState(DEFAULT_WIDGETS);
    const [showAddWidgetPopup, setShowAddWidgetPopup] = useState(false);
    const [visibleWidgets, setVisibleWidgets] = useState(DEFAULT_WIDGETS.map(w => w.id));
    const [exportLoading, setExportLoading] = useState({
        aem: false,
        word: false
    });

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const issueId = params.get("issue_id");

        if (issueId) {
            authoringService.triggerWorkfrontIngest(issueId)
                .then((data) => {
                })
                .catch((err) => {
                    console.error("❌ Workfront ingest failed:", err);
                });
        }
    }, [location.search]);

    const handleBackToHome = () => {
        navigate('/home');
    };

    const handleAddWidget = (widget) => {
        const newWidget = { ...widget, id: Date.now() };
        setCustomWidgets((prevWidgets) => {
            const newWidgets = [...prevWidgets, newWidget];
            setVisibleWidgets((prevVisible) => [...prevVisible, newWidget.id]);
            return newWidgets;
        });
        setShowAddWidgetPopup(false);
    };

    const handleDeleteWidget = (id) => {
        setCustomWidgets((prevWidgets) => prevWidgets.filter((widget) => widget.id !== id));
        setVisibleWidgets((prevVisible) => prevVisible.filter((wid) => wid !== id));
    };

    const handleTitleChange = (id, newTitle) => {
        setCustomWidgets((prevWidgets) =>
            prevWidgets.map((widget) =>
                widget.id === id ? { ...widget, title: newTitle } : widget
            )
        );
    };

    const handleContentChange = (id, newContent) => {
        setCustomWidgets((prevWidgets) =>
            prevWidgets.map((widget) =>
                widget.id === id ? { ...widget, content: newContent } : widget
            )
        );
    };

    const toggleWidgetVisibility = (widgetId) => {
        setVisibleWidgets((prev) =>
            prev.includes(widgetId)
                ? prev.filter((wid) => wid !== widgetId)
                : [...prev, widgetId]
        );
    };

    const constructAEMPayload = () => {
        const contentFragments = {};
        let paragraphCount = 1;
        let imageRefPath = "";

        for (const widget of customWidgets) {
            if (!visibleWidgets.includes(widget.id)) continue;

            const rawTitle = widget.title?.trim() || "";
            const rawContent = widget.content || "";

            // Remove double quotes
            const title = rawTitle.replace(/"/g, '');
            const content = rawContent.replace(/"/g, '');

            const lowerTitle = title.toLowerCase();

            const combined = `${title}<br/>${content}`;

            if (lowerTitle === "title") {
                contentFragments.Title = content;
            } else if (lowerTitle === "introduction") {
                contentFragments.Introduction = content;
            } else if (lowerTitle === "conclusion") {
                contentFragments.Conclusion = content;
            } else {
                contentFragments[`Paragraph${paragraphCount}`] = combined;
                paragraphCount++;
            }
        }

        return {
            content_fragments: contentFragments,
            image_path: imageRefPath
        };
    };

    const handleExportToAEM = async () => {
        try {
            setExportLoading(prev => ({ ...prev, aem: true }));
            const payload = constructAEMPayload();

            const responseText = await authoringService.exportToAEM(payload);
            alert('Content successfully published to AEM!');
        } catch (error) {
            console.error('❌ Export to AEM error:', error);
            alert('Failed to export content to AEM.');
        } finally {
            setExportLoading(prev => ({ ...prev, aem: false }));
        }
    };

    const handleExportToWordDoc = async () => {
        try {
            setExportLoading(prev => ({ ...prev, word: true }));
            const response = await authoringService.exportToWord(customWidgets);

            if (!response.ok) throw new Error("Failed to generate document");
            const today = new Date().toISOString().split("T")[0];
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${today}.docx`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("❌ Error exporting Word document:", error);
            alert("Failed to export Word document.");
        } finally {
            setExportLoading(prev => ({ ...prev, word: false }));
        }
    };

    return (
        <div className="min-h-screen bg-[#f1f1f2]">
            <AuthorHeader onBackClick={handleBackToHome} />

            <div className="flex pt-[56px]">
                <AuthorSidebar
                    widgets={customWidgets}
                    visibleWidgets={visibleWidgets}
                    onToggleVisibility={toggleWidgetVisibility}
                    onAddWidgetClick={() => setShowAddWidgetPopup(true)}
                    onExportToAEM={handleExportToAEM}
                    onExportToWord={handleExportToWordDoc}
                    exportLoading={exportLoading}
                />

                {/* Main Content */}
                <div className="ml-64 flex-1 p-6">
                    <div className="space-y-6 mb-20">
                        {customWidgets.map((widget) => (
                            visibleWidgets.includes(widget.id) && (
                                <AuthorWidgetComponent
                                    key={widget.id}
                                    id={widget.id}
                                    title={widget.title}
                                    prompt={widget.prompt}
                                    chartQuery={widget.chartQuery}
                                    onDelete={() => handleDeleteWidget(widget.id)}
                                    onTitleChange={(id, newTitle) => handleTitleChange(id, newTitle)}
                                    onContentChange={(id, newContent) => handleContentChange(id, newContent)}
                                />
                            )
                        ))}
                    </div>
                </div>
            </div>

            {/* Add Widget Popup */}
            {showAddWidgetPopup && (
                <AddAuthorWidgetPopup onAdd={handleAddWidget} onClose={() => setShowAddWidgetPopup(false)} />
            )}

            {/* Chatbot */}
            <WealthChatBot />
        </div>
    );
};

export default AuthorPage;